import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';

interface ClientOption {
  id: string;
  first_name: string;
  last_name: string;
}

interface AppointmentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  /** When provided, client is fixed (read-only). When absent, clients list is used. */
  fixedClient?: ClientOption | null;
  /** Selectable client list — used when fixedClient is not provided. */
  clients?: ClientOption[];
}

const AppointmentDialog: React.FC<AppointmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  fixedClient,
  clients = [],
}) => {
  const { cloudProvider } = useMicrospecDynamicUIContext();
  const userProfile = (cloudProvider as any).userProfile?.attributes;
  const firestore = (cloudProvider as any).firestore;

  const [selectedClientId, setSelectedClientId] = useState('');
  const [appointmentType, setAppointmentType] = useState<'frontal' | 'call'>('frontal');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedClientId('');
    setAppointmentType('frontal');
    setAppointmentDate('');
    setAppointmentTime('');
    setNotes('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const resolvedClient = fixedClient || clients.find((c) => c.id === selectedClientId) || null;
  const canSubmit = resolvedClient && appointmentDate && appointmentTime && !saving;

  const handleSubmit = useCallback(async () => {
    if (!resolvedClient || !appointmentDate || !appointmentTime) return;
    setSaving(true);
    try {
      const db = firestore.instance;
      const dateTime = new Date(`${appointmentDate}T${appointmentTime}`);
      await addDoc(collection(db, 'appointments'), {
        client_id: resolvedClient.id,
        client_name: [resolvedClient.first_name, resolvedClient.last_name].filter(Boolean).join(' '),
        type: appointmentType,
        date_time: dateTime,
        notes: notes.trim() || null,
        status: 'scheduled',
        owner_id: userProfile.uid,
        created_at: serverTimestamp(),
      });
      resetForm();
      onClose();
      onSuccess('تم إضافة الموعد بنجاح');
    } catch (e) {
      console.error('Error creating appointment', e);
    } finally {
      setSaving(false);
    }
  }, [resolvedClient, appointmentDate, appointmentTime, appointmentType, notes, firestore?.instance, userProfile?.uid, resetForm, onClose, onSuccess]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{fixedClient ? 'تحديد موعد' : 'إضافة موعد جديد'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        {fixedClient ? (
          <TextField
            label="العميل"
            fullWidth
            value={[fixedClient.first_name, fixedClient.last_name].filter(Boolean).join(' ')}
            InputProps={{ readOnly: true }}
          />
        ) : (
          <FormControl fullWidth required>
            <InputLabel>العميل</InputLabel>
            <Select
              value={selectedClientId}
              label="العميل"
              onChange={(e) => setSelectedClientId(e.target.value)}
            >
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {[c.first_name, c.last_name].filter(Boolean).join(' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth required>
          <InputLabel>نوع الموعد</InputLabel>
          <Select
            value={appointmentType}
            label="نوع الموعد"
            onChange={(e) => setAppointmentType(e.target.value as 'frontal' | 'call')}
          >
            <MenuItem value="frontal">حضوري</MenuItem>
            <MenuItem value="call">اتصال</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="التاريخ"
          type="date"
          required
          fullWidth
          value={appointmentDate}
          onChange={(e) => setAppointmentDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="الوقت"
          type="time"
          required
          fullWidth
          value={appointmentTime}
          onChange={(e) => setAppointmentTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="ملاحظات"
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>إلغاء</Button>
        <Button variant="contained" disabled={!canSubmit} onClick={handleSubmit}>
          {saving ? 'جاري الحفظ...' : 'حفظ'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppointmentDialog;
